import getEnv from '../config'
import getClient from './splitio'
import {
  DEFAULT_ACTIVE_FF,
  FF_WHITE_LIST,
  FALLBACK_FF_ON_SPLITIO_FAIL,
  CTI_RTM_OVER_PUSHER,
  CTI_CLIENT_AUTOMATIC_ACCOUNT_LOGIN
} from './whitelist'

export const isAccountRtmEnabled = (featureFlags) => featureFlags.includes(CTI_RTM_OVER_PUSHER)
export const isAutomaticAccountLoginEnabled = (featureFlags) =>
  featureFlags.includes(CTI_CLIENT_AUTOMATIC_ACCOUNT_LOGIN)

function processFlags (splitioFlags, features) {
  console.debug('[FeatureFlags] Processing Split.io flags', {
    splitioFlags,
    features
  })

  const result = features.reduce((result, flag) => {
    const treatment = splitioFlags[flag]
    const isActive = treatment === 'on'

    console.debug('[FeatureFlags] Processing flag', {
      flag,
      treatment,
      isActive
    })

    if (isActive) {
      result.push(flag.toUpperCase())
    }
    return result
  }, [])

  console.debug('[FeatureFlags] Processed flags result', {
    activeFlags: result
  })

  return result
}

function getFlags (client, features, attributes = {}) {
  return processFlags(client.getTreatments(features, attributes), features)
}

function getSplitIoFeatureFlags (trafficKey, attributes = {}) {
  console.debug('[FeatureFlags] Initializing Split.io feature flags', {
    trafficKey,
    attributes
  })

  const splitIoEnabled = getEnv('SPLIT_IO_ENABLED')
  const splitIoKey = getEnv('SPLIT_IO_KEY')

  console.debug('[FeatureFlags] Split.io configuration', {
    splitIoEnabled,
    splitIoKeyPresent: !!splitIoKey
  })

  if (!splitIoEnabled || splitIoEnabled !== 'true' || !splitIoKey) {
    console.debug('[FeatureFlags] Split.io not enabled, returning default flags', {
      defaultFlags: DEFAULT_ACTIVE_FF
    })
    return Promise.resolve(DEFAULT_ACTIVE_FF)
  }

  console.debug('[FeatureFlags] Fetching flags from Split.io')
  return getClient(splitIoKey, trafficKey)
    .then(({ client, manager }) => {
      console.debug('[FeatureFlags] Fetching flags', {
        flagList: FF_WHITE_LIST,
        attributes
      })
      const allFlags = getFlags(client, FF_WHITE_LIST, attributes)
      console.debug('[FeatureFlags] All flags retrieved successfully', {
        allFlags,
        totalCount: allFlags.length
      })

      // TODO: getSplitIoFeatureFlags calls client.destroy(), but getClient caches clients by trafficKey (clients[trafficKey] = client).
      // Destroying the client without clearing it from the cache means subsequent calls for the same trafficKey can return a destroyed
      // client instance. Either stop caching if you always destroy, or clear the cached entry (and/or avoid destroying here) to prevent reuse of an invalid client.
      client.destroy()
      return allFlags
    })
    .catch((error) => {
      console.error('[FeatureFlags] Error fetching flags from Split.io', {
        error: error.message || error,
        fallbackFlags: FALLBACK_FF_ON_SPLITIO_FAIL
      })
      return FALLBACK_FF_ON_SPLITIO_FAIL
    })
}

function getSplitIoCompatibilityEnv (prefix = 'FF_', suffix = '_SPLITIO_COMPATIBILITY') {
  if (window.env && window.env.__AZORES_CTI__) {
    return Object.keys(window.env.__AZORES_CTI__).reduce((envVars, key) => {
      if (key.startsWith(prefix) && key.endsWith(suffix)) {
        const feature = key.slice(prefix.length, key.length - suffix.length).toUpperCase()
        envVars[feature] = window.env.__AZORES_CTI__[key]
      }
      return envVars
    }, {})
  }
  return {}
}

function getEnvFeatureFlags (trafficKey) {
  console.log('[FeatureFlags] Getting environment-based feature flags', {
    trafficKey
  })

  const envFlags = getSplitIoCompatibilityEnv()
  console.log('[FeatureFlags] Environment flags loaded', {
    envFlags,
    flagCount: Object.keys(envFlags).length
  })

  return Promise.resolve(
    Object.keys(envFlags).reduce((result, key) => {
      // Support both CTI_ and EI_ prefixed flags
      const isValidPrefix = key.startsWith('CTI_') || key.startsWith('EI_')
      const isMatchingTraffic = envFlags[key] === 'all' || envFlags[key].split(',').includes(trafficKey)

      console.log('[FeatureFlags] Evaluating env flag', {
        key,
        value: envFlags[key],
        isValidPrefix,
        isMatchingTraffic,
        included: isValidPrefix && isMatchingTraffic
      })

      if (isValidPrefix && isMatchingTraffic) {
        result.push(key)
      }
      return result
    }, [])
  ).then((result) => {
    console.log('[FeatureFlags] Environment flags resolved', {
      activeFlags: result,
      count: result.length
    })
    return result
  })
}

export const getFeatureFlags = (trafficKey, attributes = {}) => {
  console.log('[FeatureFlags] Starting feature flag retrieval', {
    trafficKey,
    attributes,
    timestamp: new Date().toISOString()
  })

  const fedrampEnv = getEnv('FEDRAMP_ENV')
  console.log('[FeatureFlags] Environment check', {
    fedrampEnv,
    usingEnvFlags: fedrampEnv === 'true'
  })

  if (fedrampEnv === 'true') {
    console.log('[FeatureFlags] Using environment-based feature flags')
    return getEnvFeatureFlags(trafficKey)
  }

  console.log('[FeatureFlags] Using Split.io feature flags')
  return getSplitIoFeatureFlags(trafficKey, attributes)
}

export default getFeatureFlags
